/**
 * Extract HTML-ish content from uploaded rider/writer documents.
 */

const PDF_PARSE_TIMEOUT_MS = 90000;

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function withTimeout(promise, ms, message) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(message)), ms);
    }),
  ]);
}

function configurePdfWorker(pdfjs) {
  if (typeof pdfjs.GlobalWorkerOptions === 'undefined') return;
  const version = pdfjs.version || '5.6.205';
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;
}

async function loadPdfJs() {
  if (typeof window !== 'undefined') {
    try {
      return await import('pdfjs-dist/webpack.mjs');
    } catch (err) {
      console.warn('pdfjs webpack entry failed, falling back to manual worker', err);
    }
  }
  const pdfjs = await import('pdfjs-dist/build/pdf.mjs');
  configurePdfWorker(pdfjs);
  return pdfjs;
}

function plainTextToHtml(text) {
  const blocks = String(text || '')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
  if (!blocks.length) return '';
  return blocks
    .map((block) => {
      const lines = block.split('\n').map((line) => escapeHtml(line.trim())).filter(Boolean);
      if (lines.length === 1) return `<p>${lines[0]}</p>`;
      return `<ul>${lines.map((line) => `<li>${line}</li>`).join('')}</ul>`;
    })
    .join('');
}

function pageTextFromContent(content) {
  return (content?.items || [])
    .map((item) => {
      const chunk = String(('str' in item ? item.str : item?.str) || '');
      return item?.hasEOL ? `${chunk}\n` : chunk;
    })
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

async function assetToBlob(asset) {
  if (asset?.file instanceof Blob) return asset.file;
  const uri = asset?.uri;
  if (!uri) throw new Error('Could not read the selected file.');
  const response = await fetch(uri);
  if (!response.ok) throw new Error('Could not read the selected file.');
  return response.blob();
}

/**
 * @param {Blob | File} file
 * @returns {Promise<string>} plain text
 */
export async function importPdfToPlainText(file) {
  const pdfjs = await loadPdfJs();
  const buffer = await file.arrayBuffer();
  const doc = await withTimeout(
    pdfjs.getDocument({ data: buffer }).promise,
    PDF_PARSE_TIMEOUT_MS,
    'PDF import timed out. Try a smaller file or re-save the PDF.'
  );
  const parts = [];
  for (let pageNum = 1; pageNum <= doc.numPages; pageNum += 1) {
    const page = await doc.getPage(pageNum);
    const content = await page.getTextContent();
    const pageText = pageTextFromContent(content);
    if (pageText) parts.push(pageText);
  }
  return parts.join('\n\n').trim();
}

/**
 * @param {Blob | File} file
 * @returns {Promise<string>} HTML fragment
 */
export async function importPdfToHtml(file) {
  const plain = await importPdfToPlainText(file);
  return plainTextToHtml(plain);
}

/**
 * @param {Blob | File} file
 * @returns {Promise<string>} plain text
 */
export async function importDocxToPlainText(file) {
  const mammoth = await import('mammoth');
  const buffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer: buffer });
  return String(result?.value || '').trim();
}

/**
 * @param {Blob | File} file
 * @returns {Promise<string>} HTML fragment
 */
export async function importDocxToHtml(file) {
  const mammoth = await import('mammoth');
  const buffer = await file.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
  return String(result?.value || '').trim();
}

function detectDocumentKind(name, mime) {
  const fileName = String(name || '').toLowerCase();
  const mimeType = String(mime || '').toLowerCase();
  if (mimeType.includes('pdf') || fileName.endsWith('.pdf')) return 'pdf';
  if (
    mimeType.includes('wordprocessingml') ||
    mimeType.includes('msword') ||
    fileName.endsWith('.docx')
  ) {
    return 'docx';
  }
  if (fileName.endsWith('.doc')) return 'doc';
  return '';
}

/**
 * @param {{ uri?: string, name?: string, mimeType?: string, file?: Blob }} asset from DocumentPicker
 * @returns {Promise<string>} plain text
 */
export async function importDocumentAssetToPlainText(asset) {
  const blob = await assetToBlob(asset);
  const kind = detectDocumentKind(asset?.name, asset?.mimeType);
  if (kind === 'pdf') return importPdfToPlainText(blob);
  if (kind === 'docx') return importDocxToPlainText(blob);
  if (kind === 'doc') {
    throw new Error('Legacy .doc files are not supported. Save as .docx or PDF and try again.');
  }
  throw new Error('Upload a PDF or Word (.docx) document.');
}

/**
 * @param {{ uri?: string, name?: string, mimeType?: string, file?: Blob }} asset from DocumentPicker
 * @returns {Promise<string>}
 */
export async function importDocumentAssetToHtml(asset) {
  const blob = await assetToBlob(asset);
  const kind = detectDocumentKind(asset?.name, asset?.mimeType);
  if (kind === 'pdf') return importPdfToHtml(blob);
  if (kind === 'docx') return importDocxToHtml(blob);
  if (kind === 'doc') {
    throw new Error('Legacy .doc files are not supported. Save as .docx or PDF and try again.');
  }
  throw new Error('Upload a PDF or Word (.docx) document.');
}

export function isImageFileUrl(url = '') {
  const s = String(url || '').toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif|tiff?|jfif)(\?|#|$)/i.test(s)) return true;
  if (/[?&]content-type=image%2f/i.test(s)) return true;
  return false;
}

export function isPdfFileUrl(url = '', name = '') {
  const s = String(url || '').toLowerCase();
  const n = String(name || '').toLowerCase();
  return /\.pdf(\?|#|$)/i.test(s) || n.endsWith('.pdf');
}

/**
 * Render the first page of a PDF URL to a JPEG data URL (web only).
 * @param {string} sourceUrl
 * @param {number} maxWidth
 * @returns {Promise<string>}
 */
export async function renderPdfThumbnailDataUrl(sourceUrl, maxWidth = 240) {
  if (typeof document === 'undefined') {
    throw new Error('PDF thumbnails require a browser environment.');
  }
  const pdfjs = await loadPdfJs();
  const response = await fetch(sourceUrl);
  if (!response.ok) throw new Error('Could not load PDF for preview.');
  const buffer = await response.arrayBuffer();
  const doc = await withTimeout(
    pdfjs.getDocument({ data: buffer }).promise,
    30000,
    'PDF preview timed out.'
  );
  const page = await doc.getPage(1);
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(2, maxWidth / Math.max(baseViewport.width, 1));
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create preview canvas.');
  await page.render({ canvasContext: context, viewport }).promise;
  return canvas.toDataURL('image/jpeg', 0.88);
}
